import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { addToBag } from './bag';
import { getOptions, lineFor } from './productOptions';
import { autoPick, isComplete } from './OptionPicker';
import { toggleWishlist, useWishlist } from './savedLists';
import { openBag, openQuickView } from './ui';
import { formatInr, retailPrice } from './retailUtils';
import { HeartIcon } from './Icons';

const LOW_STOCK = 5;
// Rendered card width: 2 per row on phones, up to 4 per row on desktop.
const CARD_SIZES = '(min-width: 960px) 25vw, 50vw';

export function WishlistButton({ productId, className = 'rt-card-heart', size = 18 }) {
  const saved = useWishlist().some(id => String(id) === String(productId));
  return (
    <button type="button" className={`${className}${saved ? ' is-on' : ''}`} aria-pressed={saved}
      aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
      onClick={e => { e.preventDefault(); e.stopPropagation(); toggleWishlist(productId); }}>
      <HeartIcon filled={saved} size={size} />
    </button>
  );
}

/** Adds straight to the bag when there's nothing to choose; otherwise opens quick view
 *  so the shopper picks a size without leaving the list. */
export function quickAdd(product) {
  const options = getOptions(product);
  const { color, size } = autoPick(options);
  if (!isComplete(options, color, size)) { openQuickView(product); return; }
  const line = lineFor(product, options, color, size);
  addToBag({ ...line, quantity: 1 }, line.stock ?? 99);
  openBag();
}

/** Honest tags only: each reflects real data (stock, or among the newest additions). */
function tagFor(product, isNew) {
  if (product.itemsAvailable === 0) return 'Sold out';
  if (product.itemsAvailable != null && product.itemsAvailable <= LOW_STOCK) return 'Few left';
  if (isNew) return 'New';
  return null;
}

export default function ProductCard({ product, priority = false, isNew = false }) {
  const { price, mrp, hasDiscount, discountPercent } = retailPrice(product);
  const outOfStock = product.itemsAvailable === 0;
  const tag = tagFor(product, isNew);
  // The second photo is only requested once the shopper actually hovers.
  const [showHover, setShowHover] = useState(false);
  const colors = getOptions(product).colors.filter(c => c.available);

  return (
    <div className={`rt-card${outOfStock ? ' rt-card--oos' : ''}`}
      onMouseEnter={() => product.hoverImageUrl && setShowHover(true)}>
      <div className="rt-card-media">
        <Link to={`/product/${product.id}`} className="rt-card-img" tabIndex={-1} aria-hidden="true">
          {product.thumbnailUrl
            ? <img src={product.thumbnailUrl} srcSet={product.thumbnailSrcset} sizes={CARD_SIZES} alt="" loading={priority ? 'eager' : 'lazy'} decoding="async" className="rt-card-img-main" />
            : <div className="rt-card-noimg" />}
          {showHover && <img src={product.hoverImageUrl} srcSet={product.hoverImageSrcset} sizes={CARD_SIZES} alt="" decoding="async" className="rt-card-img-hover" />}
          {tag && <span className={`rt-card-tag${outOfStock ? ' rt-card-tag--oos' : ''}`}>{tag}</span>}
        </Link>
        <WishlistButton productId={product.id} />
        {!outOfStock && (
          <button type="button" className="rt-card-add" aria-label={`Add ${product.name} to bag`} onClick={() => quickAdd(product)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            <span className="rt-card-add-label">Quick add</span>
          </button>
        )}
      </div>
      <Link to={`/product/${product.id}`} className="rt-card-link">
        <div className="rt-card-body">
          <div className="rt-card-name">{product.name}</div>
          {product.brandName && <div className="rt-card-brand">{product.brandName}</div>}
          <div className="rt-price">
            <span className="rt-price-now">{formatInr(price)}</span>
            {hasDiscount && <>
              <span className="rt-price-mrp"><span className="rt-sr-only">MRP </span>{formatInr(mrp)}</span>
              <span className="rt-price-off">{discountPercent}% off</span>
            </>}
          </div>
          {colors.length > 1 && (
            <div className="rt-card-colors" aria-label={`${colors.length} colours`}>
              {colors.slice(0, 5).map(c => <span key={c.name} style={{ background: c.hex }} title={c.label} />)}
              {colors.length > 5 && <em>+{colors.length - 5}</em>}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

/** Horizontally scrolling row of cards (home rails, "similar", "recently viewed"). */
export function ProductRail({ title, products, action, newIds }) {
  if (!products.length) return null;
  return (
    <section className="rt-rail" aria-label={title}>
      <div className="rt-rail-head">
        <h2>{title}</h2>
        {action}
      </div>
      <div className="rt-rail-track">
        {products.map(p => <div key={p.id} className="rt-rail-item"><ProductCard product={p} isNew={newIds?.has(p.id)} /></div>)}
      </div>
    </section>
  );
}
